import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Mail, Smartphone } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { authRepo } from '@/lib/repositories'
import { requireSupabaseClient } from '@/lib/supabase'
import { toE164 } from '@/lib/utils/phoneUtils'
import { useTranslation } from 'react-i18next'
import { analytics } from '@/lib/services/analytics'
import { AnalyticsEvents } from '@/lib/services/analytics-events'

export function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [otpSent, setOtpSent] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleMagicLink = async () => {
    if (!email) return

    try {
      setLoading(true)

      analytics.capture(AnalyticsEvents.AUTH_LOGIN_STARTED, { method: 'magic_link' })

      // Login via repository
      const result = await authRepo.login(email)

      if (result.success) {
        // Show success toast
        toast.success(t('magicLinkSent'), {
          description: t('checkInbox'),
        })
      }
    } catch (error) {
      console.error('Login failed:', error)

      // Check for specific error messages
      const errorMessage =
        error instanceof Error ? error.message.toLowerCase() : ''

      if (errorMessage.includes('rate limit')) {
        toast.error(t('tooManyAttempts'), {
          description: t('waitBeforeRetrying'),
        })
      } else {
        toast.error(t('loginFailed'), {
          description:
            error instanceof Error ? error.message : t('pleaseTryAgain'),
        })
      }
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && email && !loading) {
      handleMagicLink()
    }
  }

  const handlePhoneOtp = async () => {
    if (!phone) return

    try {
      setLoading(true)
      const formatted = toE164(phone)

      // Verify phone exists in profiles before sending OTP (sign-in only, no signup)
      const supabase = requireSupabaseClient()
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('username')
        .eq('phone_number', formatted)
        .single()

      if (!existingProfile) {
        toast.error(t('noAccountFound'), {
          description: t('noAccountFoundDesc'),
        })
        setLoading(false)
        return
      }

      analytics.capture(AnalyticsEvents.AUTH_LOGIN_STARTED, { method: 'phone_otp' })
      const result = await authRepo.login(formatted)

      if (result.success) {
        setOtpSent(true)
        toast.success(t('otpSent'), {
          description: t('checkPhone'),
        })
      }
    } catch (error) {
      console.error('Phone login failed:', error)
      const errorMessage =
        error instanceof Error ? error.message.toLowerCase() : ''

      if (errorMessage.includes('rate limit')) {
        toast.error(t('tooManyAttempts'), {
          description: t('waitBeforeRetrying'),
        })
      } else {
        toast.error(t('loginFailed'), {
          description:
            error instanceof Error ? error.message : t('pleaseTryAgain'),
        })
      }
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyOtp = async () => {
    if (!otpCode || !phone) return

    try {
      setLoading(true)
      const supabase = (await import('@/lib/supabase')).requireSupabaseClient()
      const formatted = toE164(phone)

      const { error } = await supabase.auth.verifyOtp({
        phone: formatted,
        token: otpCode,
        type: 'sms',
      })

      if (error) {
        throw new Error(error.message)
      }

      analytics.capture(AnalyticsEvents.AUTH_LOGIN_SUCCESS, { method: 'phone_otp' })
      toast.success(t('loginSuccess'))
      navigate('/feed')
    } catch (error) {
      console.error('OTP verification failed:', error)
      toast.error(t('invalidOtp'), {
        description:
          error instanceof Error ? error.message : t('pleaseTryAgain'),
      })
    } finally {
      setLoading(false)
    }
  }

  const handlePhoneKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      if (otpSent) {
        handleVerifyOtp()
      } else if (phone) {
        handlePhoneOtp()
      }
    }
  }

  const handleGoogleLogin = async () => {
    try {
      setLoading(true)
      analytics.capture(AnalyticsEvents.AUTH_LOGIN_STARTED, { method: 'google' })
      await authRepo.loginWithGoogle()
    } catch (error) {
      console.error('Google login failed:', error)
      toast.error(t('googleLoginFailed'), {
        description:
          error instanceof Error ? error.message : t('pleaseTryAgain'),
      })
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-sovereign-mesh flex items-center justify-center p-4">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSA2MCAwIEwgMCAwIDAgNjAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAyKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-50" />

      <Card className="w-full max-w-md border-white/[0.06] bg-neutral-950/80 backdrop-blur-xl relative z-10">
        <CardHeader className="text-center space-y-3">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-2xl flex items-center justify-center mb-2 shadow-lg shadow-emerald-500/20">
            <span
              className="text-black text-6xl"
              style={{
                fontFamily: "'garamond-atf-text', 'EB Garamond', serif",
                fontStyle: 'italic',
                WebkitTextStroke: '1px black',
              }}
            >
              S
            </span>
          </div>
          <CardTitle className="text-white text-xl tracking-tight">
            {t('welcomeToSovereign')}
          </CardTitle>
          <CardDescription className="text-neutral-400">
            {t('tradingInsightsAndAlerts')}
            <br />
            {t('noPasswordNeeded')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="email" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-neutral-900/80 border border-white/[0.06]">
              <TabsTrigger
                value="email"
                className="data-[state=active]:bg-neutral-800 data-[state=active]:text-white text-neutral-400 transition-colors"
              >
                <Mail className="w-4 h-4 mr-2" />
                {t('email')}
              </TabsTrigger>
              <TabsTrigger
                value="phone"
                className="data-[state=active]:bg-neutral-800 data-[state=active]:text-white text-neutral-400 transition-colors"
              >
                <Smartphone className="w-4 h-4 mr-2" />
                {t('phone')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="email" className="space-y-4">
              <div className="space-y-2">
                <Input
                  type="email"
                  placeholder={t('enterYourEmail')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="bg-neutral-900/80 border-white/[0.08] text-white placeholder:text-neutral-500 focus:border-emerald-500/50 focus:ring-emerald-500/20"
                  disabled={loading}
                />
                {/* <p className="text-xs text-neutral-500 font-mono">
                  Try: admin@sovereign.com for admin access
                </p> */}
              </div>
              <Button
                onClick={handleMagicLink}
                disabled={!email || loading}
                className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-black font-semibold shadow-lg shadow-emerald-500/25 transition-all hover:shadow-emerald-500/40"
              >
                {loading ? t('sending') : t('sendMagicLink')}
              </Button>
            </TabsContent>

            <TabsContent value="phone" className="space-y-4">
              {!otpSent ? (
                <>
                  <div className="space-y-2">
                    <Input
                      type="tel"
                      placeholder={t('enterPhoneNumber')}
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      onKeyDown={handlePhoneKeyDown}
                      className="bg-neutral-900/80 border-white/[0.08] text-white placeholder:text-neutral-500 focus:border-emerald-500/50 focus:ring-emerald-500/20"
                      disabled={loading}
                    />
                  </div>
                  <Button
                    onClick={handlePhoneOtp}
                    disabled={!phone || loading}
                    className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-black font-semibold shadow-lg shadow-emerald-500/25 transition-all hover:shadow-emerald-500/40"
                  >
                    {loading ? t('sending') : t('sendOtpCode')}
                  </Button>
                </>
              ) : (
                <>
                  <div className="space-y-2">
                    <p className="text-xs text-neutral-400">
                      {t('otpSentTo')} <span className="text-white font-mono">{phone}</span>
                    </p>
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder={t('enterOtpCode')}
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      onKeyDown={handlePhoneKeyDown}
                      className="bg-neutral-900/80 border-white/[0.08] text-white placeholder:text-neutral-500 focus:border-emerald-500/50 focus:ring-emerald-500/20 text-center text-2xl tracking-[0.5em] font-mono"
                      disabled={loading}
                      maxLength={6}
                      autoFocus
                    />
                  </div>
                  <Button
                    onClick={handleVerifyOtp}
                    disabled={otpCode.length < 6 || loading}
                    className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-black font-semibold shadow-lg shadow-emerald-500/25 transition-all hover:shadow-emerald-500/40"
                  >
                    {loading ? t('verifying') : t('verifyCode')}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => { setOtpSent(false); setOtpCode('') }}
                    disabled={loading}
                    className="w-full text-neutral-400 hover:text-white"
                  >
                    {t('changePhoneNumber')}
                  </Button>
                </>
              )}
            </TabsContent>
          </Tabs>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/[0.06]" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-3 bg-neutral-950/80 text-neutral-500">
                {t('orContinueWith')}
              </span>
            </div>
          </div>

          {/* Google Sign In */}
          <Button
            variant="outline"
            onClick={handleGoogleLogin}
            disabled={loading}
            className="w-full bg-neutral-900/50 border-white/[0.08] text-white hover:bg-neutral-800 hover:border-white/[0.12] transition-all"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {t('continueWithGoogle')}
          </Button>

          <div className="mt-6 pt-6 border-t border-white/[0.06]">
            <p className="text-xs text-center text-neutral-500">
              {t('passwordlessAuth')}
              <br />
              {t('loginLinkExpires')}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
